import React, { ChangeEvent, FC, useCallback, useRef, useState, useMemo } from 'react'
import { Button, Heading, Icon, Modal, Text, Dropdown, Input, InputBirthday } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import { SupportedLocale } from '@afterpay/types'
import { isDateInvalid } from '../../../utils'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { Section75DisputeModalSteps, SelectedItem, useSection75DisputeModalContext } from '../../Section75DisputeModalContext'
import { MAX_FILE_SIZE } from '../../../../../model'
import { DISPUTE_REASON_OPTIONS, DisputeReason } from '../../types'
import { useTranslation } from 'next-i18next'
import styles from './DisputeDetails.module.scss'

const I18N_NAMESPACE = ['common', 'dispute']

export const DisputeDetails: FC = () => {
  const { textSize } = useConsumerSizes()
  const { t } = useTranslation(I18N_NAMESPACE)
  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const {
    setStep,
    selectedItems,
    setSelectedItems,
    files,
    setFiles,
    order,
    expectedDeliveryDate,
    setExpectedDeliveryDate,
    currentItemIndex,
    setCurrentItemIndex,
    setReason,
  } = useSection75DisputeModalContext()

  const uploadRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string>('')

  const currentItem = currentItemIndex !== null ? selectedItems[currentItemIndex] : undefined
  const selectedItem = currentItem?.name ?? 'No item selected'
  const shouldShowExpectedDeliveryDate = currentItem?.reason === DisputeReason.PRODUCT_NOT_RECEIVED

  const updateCurrentItem = useCallback(
    (updates: (item: SelectedItem) => Partial<SelectedItem>) => {
      if (currentItemIndex === null) {
        return
      }

      setSelectedItems((prev) => {
        const current = prev[currentItemIndex]
        if (current === undefined) return prev

        const updated = [...prev]
        updated[currentItemIndex] = {
          ...current,
          ...updates(current),
        }
        return updated
      })
    },
    [currentItemIndex, setSelectedItems]
  )

  const backHandler = useCallback(() => {
    if (currentItemIndex !== null && currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1)
      setStep(Section75DisputeModalSteps.DisputeAmount)
    } else {
      setCurrentItemIndex(null)
      setStep(Section75DisputeModalSteps.ChooseItems)
    }
  }, [setStep, currentItemIndex, setCurrentItemIndex])

  const handleNext = useCallback(() => {
    setStep(Section75DisputeModalSteps.DisputeAmount)
  }, [setStep])

  const addFileButtonClickHandler = useCallback(() => {
    uploadRef.current?.click()
  }, [])

  const fileChangedHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // @ts-ignore: OPERATION BLEED STOPPER
      const fileList = Array.from(event.target.files)

      const fileOutOfSizeList = fileList.filter((file: File) => file.size > MAX_FILE_SIZE)
      if (fileOutOfSizeList.length > 0) {
        setError(t('dispute:submitEvidence:error:exceedSize'))
        return
      }

      setFiles([...files, ...fileList])

      const newFileNames = fileList.map((file) => file.name)
      updateCurrentItem((item) => ({
        fileKeys: [...(item.fileKeys ?? []), ...newFileNames],
      }))

      setError('')
      event.target.value = ''
    },
    [files, setFiles, t, updateCurrentItem]
  )

  const reasonChangeHandler = useCallback(
    (reason) => {
      const trimmedReason = reason.trim()

      updateCurrentItem(() => ({
        reason: trimmedReason,
      }))

      if (currentItemIndex === 0) {
        setReason(trimmedReason)
      }
    },
    [currentItemIndex, setReason, updateCurrentItem]
  )

  const expectedDeliveryDateChangeHandler = useCallback(
    (e, date): void => {
      setExpectedDeliveryDate(date ?? null)
    },
    [setExpectedDeliveryDate]
  )

  const expectedDeliveryDateBlurHandler = useCallback(
    (e, date): void => {
      setExpectedDeliveryDate(date ?? null)
    },
    [setExpectedDeliveryDate]
  )

  const noteChangeHandler = useCallback(
    (e) => {
      const description = e.target.value

      updateCurrentItem(() => ({
        description,
      }))
    },
    [updateCurrentItem]
  )

  const descriptionLabel = useMemo(() => {
    const reason = currentItem?.reason
    const defaultKey = 'dispute:section75DisputeModal:disputeDetails:descriptionLabel'

    if (reason !== undefined && reason !== '') {
      const reasonKey = `${defaultKey}.${reason}`
      const translated = t(reasonKey, { defaultValue: '' })
      if (translated !== '') {
        return translated
      }
    }

    return t(defaultKey)
  }, [currentItem?.reason, t])

  React.useEffect(() => {
    if (currentItemIndex === null && selectedItems.length > 0) {
      setCurrentItemIndex(0)
    }
  }, [currentItemIndex, selectedItems, setCurrentItemIndex])

  const expectedDeliveryDateInvalid = useMemo<boolean>(() => {
    return shouldShowExpectedDeliveryDate && expectedDeliveryDate !== null && isDateInvalid(expectedDeliveryDate, order)
  }, [expectedDeliveryDate, order, shouldShowExpectedDeliveryDate])

  const dateErrorMsg = t('dispute:contactMerchantInfo.error.invalidDate')

  const nextBtnDisabled = useMemo<boolean>(() => {
    if (shouldShowExpectedDeliveryDate && (expectedDeliveryDateInvalid || expectedDeliveryDate === null)) {
      return true
    }
    if (currentItem?.reason === undefined || currentItem.reason === '') {
      return true
    }
    if (currentItem?.description === undefined || currentItem.description.trim() === '') {
      return true
    }
    return false
  }, [expectedDeliveryDateInvalid, expectedDeliveryDate, shouldShowExpectedDeliveryDate, currentItem?.reason, currentItem?.description])

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:disputeDetails:heading')}</Heading>
          {selectedItems.length > 1 && currentItemIndex !== null && (
            <Text size='S' className='mt-1 text-gray-600'>
              {t('dispute:section75DisputeModal:disputeDetails:itemCount', { current: currentItemIndex + 1, total: selectedItems.length })}
            </Text>
          )}
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className='space-y-6'>
          <div>
            <Heading size='L'>{selectedItem}</Heading>
          </div>

          <div>
            <Dropdown
              key={`dispute-reason-${currentItem?.key ?? currentItemIndex ?? 'none'}`}
              label={t('dispute:section75DisputeModal:disputeDetails:disputeReasonLabel')}
              size='Large'
              onSelect={reasonChangeHandler}
              defaultValue={currentItem?.reason ?? ''}
            >
              {DISPUTE_REASON_OPTIONS.map((option) => (
                <Dropdown.MenuItem key={option.id} value={option.value}>
                  {t(`dispute:section75DisputeModal:disputeDetails:reason:${option.value}`)}
                </Dropdown.MenuItem>
              ))}
            </Dropdown>
          </div>

          {shouldShowExpectedDeliveryDate && (
            <InputBirthday
              size='Large'
              onChange={expectedDeliveryDateChangeHandler}
              onBlur={expectedDeliveryDateBlurHandler}
              defaultValue={expectedDeliveryDate ?? undefined}
              locale={locale}
              minAge={Number.NEGATIVE_INFINITY}
              maxAge={Number.POSITIVE_INFINITY}
              error={expectedDeliveryDateInvalid}
              errorText={dateErrorMsg}
              name='expectedDeliveryDate'
              label={t('dispute:section75DisputeModal:disputeDetails:dateLabel')}
              hasDateFormatLabel={false}
            />
          )}

          <div className={styles.textArea}>
            <Input.TextArea label={descriptionLabel} size='Large' value={currentItem?.description ?? ''} onChange={noteChangeHandler} rows={4} />
          </div>

          <div>
            <Text size={textSize} bold>
              {t('dispute:section75DisputeModal:disputeDetails:supportDocs')}
            </Text>
            <Text className='mt-1 text-gray-600'>{t('dispute:section75DisputeModal:disputeDetails.upload')}</Text>

            <div className='mt-3'>
              <Button.Ghost size='Regular' padding='Fluid' onClick={addFileButtonClickHandler}>
                {t('dispute:section75DisputeModal:disputeDetails:chooseFiles')}
              </Button.Ghost>
              <input type='file' className='hidden' multiple onChange={fileChangedHandler} accept='image/*,.pdf,.doc,.docx' ref={uploadRef} />
            </div>
            {files.length > 0 && (
              <div className='mt-3'>
                <Text className='text-gray-600'>{t('dispute:section75DisputeModal:disputeDetails:uploaded')}</Text>
                <ul className='mt-1'>
                  {files.map((file, index) => (
                    <li key={index} className='text-sm text-gray-700'>
                      <Text className='text-gray-600'>{file.name}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {error !== '' && (
              <div className='mt-3'>
                <Text color='Fire'>{error}</Text>
              </div>
            )}
          </div>
        </div>

        <div className='mt-6'>
          <Button padding='Fluid' size='Large' onClick={handleNext} disabled={nextBtnDisabled}>
            {t('dispute:section75DisputeModal:disputeDetails:next')}
          </Button>
        </div>
      </Modal.Content>
    </>
  )
}
